import type { ValidEndpoints } from '@/api/clients/backend-client'
import type { SavedRouteData, SavedRouteItineraryData } from '@/types/trip_specific_types'
import { process_number_or_null, type CrudConverter } from './converter-helpers'
import { createItem, deleteItem, fetchCollection, updateItem, type ApiResult } from './helper-functions'

const collectionName: ValidEndpoints = 'route_plans'

type Route_plan_expected_API_DTO = {
  id: string
  user_created: string
  vehicle_id: string
  itinerary: Record<string, unknown>
  date: string // Timestamp
  name?: string | null
  planning_parameters?: Record<string, unknown> | null
  assigned_driver: string
}

type ParsedPlanningParameters = {
  accelerationAdjustment: number | null
  arrivalSOC: number
  departingLoad: number
  departingSOC: number
  maximumSOC: number
  minimumSOC: number
  passengerCount: number
  speedAdjustment: number | null
}

const parse_planning_parameters = (
  planning_parameters?: Record<string, unknown> | null,
): ParsedPlanningParameters => {
  const template_params = {
    accelerationAdjustment: 0,
    arrivalSOC: 0.2,
    departingLoad: 0,
    departingSOC: 1,
    maximumSOC: 0.9,
    minimumSOC: 0.1,
    passengerCount: 0,
    speedAdjustment: 0,
  }

  if (!planning_parameters) return template_params

  return {
    accelerationAdjustment:
      process_number_or_null(planning_parameters.acceleration_adjustment) ?? 0,
    arrivalSOC: process_number_or_null(planning_parameters.arrival_SoC) ?? 0.2,
    departingLoad: process_number_or_null(planning_parameters.departing_load_kg) ?? 0,
    departingSOC: process_number_or_null(planning_parameters.departing_SoC) ?? 1,
    maximumSOC: process_number_or_null(planning_parameters.maximum_SoC) ?? 0.9,
    minimumSOC: process_number_or_null(planning_parameters.minimum_SoC) ?? 0.1,
    passengerCount: process_number_or_null(planning_parameters.passenger_count) ?? 0,
    speedAdjustment: process_number_or_null(planning_parameters.speed_adjustment) ?? 0,
  }
}

const process_planning_parameters = (
  legacy_obj: Partial<Directus_SavedRoutePlan>,
): Record<string, unknown> | null => {
  const capture_obj: Record<string, unknown> = {}

  if (legacy_obj.accelerationAdjustment)
    capture_obj.acceleration_adjustment = legacy_obj.accelerationAdjustment
  if (legacy_obj.arrivalSOC) capture_obj.arrival_SoC = legacy_obj.arrivalSOC
  if (legacy_obj.departingLoad) capture_obj.departing_load_kg = legacy_obj.departingLoad
  if (legacy_obj.departingSOC) capture_obj.departing_SoC = legacy_obj.departingSOC
  if (legacy_obj.maximumSOC) capture_obj.maximum_SoC = legacy_obj.maximumSOC
  if (legacy_obj.minimumSOC) capture_obj.minimum_SoC = legacy_obj.minimumSOC
  if (legacy_obj.passengerCount) capture_obj.passenger_count = legacy_obj.passengerCount
  if (legacy_obj.speedAdjustment) capture_obj.speed_adjustment = legacy_obj.speedAdjustment

  if (Object.keys(capture_obj).length) return capture_obj
  return null
}

const tripConverter: CrudConverter<
  Route_plan_expected_API_DTO,
  Directus_SavedRoutePlan,
  Partial<
    Omit<
      Directus_SavedRoutePlan,
      'id' | 'user_created' | 'date_created' | 'user_updated' | 'date_updated'
    >
  >,
  Partial<
    Omit<
      Directus_SavedRoutePlan,
      'id' | 'user_created' | 'date_created' | 'user_updated' | 'date_updated'
    >
  >
> = {
  apiToLegacy(api) {
    return {
      ...parse_planning_parameters(api.planning_parameters),
      chargingTime: 0,
      date_created: '',
      date_updated: '',
      defaultDataUsedFlag: false,
      distance: 0,
      drivingTime: 0,
      energyUsed: 0,
      ferryTime: 0,
      id: api.id,
      itinerary: api.itinerary as unknown as SavedRouteItineraryData,
      roundTripFlag: false,
      totalTime: 0,
      user_created: api.user_created,
      user_updated: '',
      waypoints: [],
      energyAdded: 0,
      frequency: null,
      name: api.name ?? undefined,
      vehicleEVModel: undefined,
      vehicleId: api.vehicle_id,
    }
  },
  legacyToApi(legacy) {
    return {
      assigned_driver: '', // TODO: implement assigning a driver
      date: '', // TODO: implement departure date
      id: legacy.id.toString(),
      itinerary: legacy.itinerary as unknown as Record<string, unknown>,
      user_created: legacy.user_created ?? '',
      vehicle_id: legacy.vehicleId?.toString() ?? '',
      name: legacy.name,
      planning_parameters: {
        acceleration_adjustment: legacy.accelerationAdjustment,
        arrival_SoC: legacy.arrivalSOC,
        departing_load_kg: legacy.departingLoad,
        departing_SoC: legacy.departingSOC,
        maximum_SoC: legacy.maximumSOC,
        minimum_SoC: legacy.minimumSOC,
        passenger_count: legacy.passengerCount,
        speed_adjustment: legacy.speedAdjustment,
      } as unknown as Record<string, unknown>,
    }
  },
  createToApi(create) {
    return {
      assigned_driver: '', // TODO: implement assigning a driver
      date: '', // TODO: implement departure date
      itinerary: create.itinerary as unknown as Record<string, unknown>,
      vehicle_id: create.vehicleId?.toString() ?? '',
      name: create.name,
      planning_parameters: {
        acceleration_adjustment: create.accelerationAdjustment,
        arrival_SoC: create.arrivalSOC,
        departing_load_kg: create.departingLoad,
        departing_SoC: create.departingSOC,
        maximum_SoC: create.maximumSOC,
        minimum_SoC: create.minimumSOC,
        passenger_count: create.passengerCount,
        speed_adjustment: create.speedAdjustment,
      } as unknown as Record<string, unknown>,
    }
  },
  updateToApi(update) {
    const capture_obj: Partial<Route_plan_expected_API_DTO> = {}
    // TODO:  handle assigned driver
    // TODO: handle assigned data
    if (update.itinerary)
      capture_obj.itinerary = update.itinerary as unknown as Record<string, unknown>
    if (update.name !== undefined) capture_obj.name = update.name // allow null
    const processed_planning_data = process_planning_parameters(update)
    if (processed_planning_data) capture_obj.planning_parameters = processed_planning_data
    if (update.vehicleId) capture_obj.vehicle_id = update.vehicleId.toString()

    return capture_obj
  },
}

/**
 * Fetches all route plans for the users fleet
 *
 * @returns a list of saved route plans if successful, undefined if not.
 */
export async function fetchFleetsRoutePlans(): Promise<Directus_SavedRoutePlan[] | undefined> {
  return await fetchCollection<Route_plan_expected_API_DTO, Directus_SavedRoutePlan>(
    collectionName,
    undefined,
    tripConverter.apiToLegacy,
  )
}

/**
 * Create a new directus `SavedRoutePlans` collection record.
 *
 * @param saveData partial object to create
 * @returns the newly created record if successful, undefined if not.
 */
export async function createSavedRoutePlan(
  saveData: Partial<Directus_SavedRoutePlan>,
): Promise<ApiResult<Directus_SavedRoutePlan>> {
  return await createItem<Route_plan_expected_API_DTO, Directus_SavedRoutePlan>(
    collectionName,
    tripConverter.createToApi(saveData),
    tripConverter.apiToLegacy,
  )
}

/**
 * Updates an existing directus `SavedRoutePlans` collection record.
 *
 * @param recordId the directus `SavedRoutePlans` collection record id.
 * @param data partial object to update
 * @returns the updated record if successful, undefined if not.
 */
export async function updatedSavedRoutePlan(
  recordId: string | number,
  data: Partial<Directus_SavedRoutePlan>,
): Promise<Directus_SavedRoutePlan | undefined> {
  return await updateItem<Directus_SavedRoutePlan>({
    endpoint: collectionName,
    query: {
      id: recordId.toString(),
    },
    body: tripConverter.updateToApi(data),
    converter: tripConverter.apiToLegacy,
  })
}

/**
 * Delete the target record from the directus `SavedRoutePlans`
 * collection.
 *
 * @param directusId the record id of the target record.
 * @returns status message outlining the outcome of the operation.
 */
export async function deleteSavedRoutePlans(directusId: string | number): Promise<'ok' | 'failed'> {
  const res = await deleteItem(collectionName, directusId.toString())
  return res ? 'ok' : 'failed'
}

export interface Directus_SavedRoutePlan extends SavedRouteData {
  id: string | number
  user_created: string | null
  date_created: string | null
  user_updated: string | null
  date_updated: string | null
}
